/**
 * PaymentFailureBanner
 *
 * Displays contextual warnings when a recurring payment has failed.
 *
 * - PAST_DUE: Warning banner — payment failed but Stripe is retrying.
 * - UNPAID: Critical banner — all retries exhausted, recurring top-up suspended.
 *
 * Provides a link to the Stripe Customer Portal so the user can update
 * their payment method directly.
 */

import type { FC } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'

interface PaymentFailureBannerProps {
  status: 'PAST_DUE' | 'UNPAID'
  onUpdatePayment: () => void
  className?: string
}

export const PaymentFailureBanner: FC<PaymentFailureBannerProps> = ({
  status,
  onUpdatePayment,
  className,
}) => {
  if (status === 'PAST_DUE') {
    return (
      <div
        className={[
          'flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Icon
          name="alert-triangle"
          className="h-5 w-5 text-amber-500 shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
            Payment failed
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400/80 mt-0.5">
            Your last payment didn't go through. Please update your payment method to avoid service disruption.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onUpdatePayment}
          className="shrink-0 border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
        >
          Update payment
        </Button>
      </div>
    )
  }

  // UNPAID — critical state
  return (
    <div
      className={[
        'flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon
        name="alert-triangle"
        className="h-5 w-5 text-destructive shrink-0 mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-destructive">
          Recurring payment failed
        </p>
        <p className="text-sm text-destructive/80 mt-0.5">
          Your recurring top-up has been suspended due to failed payments. Update your payment method to resume automatic top-ups.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onUpdatePayment}
        className="shrink-0 border-destructive/30 text-destructive hover:bg-destructive/10"
      >
        Update payment
      </Button>
    </div>
  )
}
